// 역할: 헤더 영역 (로고, 네비게이션, 언어 전환 등) - Refactored
import { useState, useEffect, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { mainMenuItems, getFilteredSubItems } from '@/config/headerMenu';
import { getPortalLinks } from '@/config/portalLinks';

import { useLanguage } from '../../hooks/useLanguage';

import { LanguageSelector } from './LanguageSelector';
import { NavItem } from './NavItem';

interface HeaderAreaProps {
  hideBottomBorder?: boolean;
}

export const HeaderArea = ({ hideBottomBorder = false }: HeaderAreaProps = {}) => {
  const location = useLocation();
  const isMainPage = location.pathname === '/' || location.pathname === '/en/';

  const [isHeaderWhite, setIsHeaderWhite] = useState(!isMainPage);
  const headerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const { changeLanguage, isOverseas } = useLanguage();
  const portalLinks = useMemo(() => getPortalLinks(i18n.language, isOverseas), [i18n.language, isOverseas]);

  // Scroll handler for header background
  useEffect(() => {
    const handleScroll = () => {
      if (isMainPage) {
        setIsHeaderWhite(window.scrollY > 0);
      } else {
        setIsHeaderWhite(true);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMainPage]);

  // Resize handler to close mobile menu on desktop
  useEffect(() => {
    const syncMenuMode = () => {
      const gnbWrap = document.getElementById('gnb_wrap');
      if (!gnbWrap) return;
      if (window.innerWidth >= 1280) {
        gnbWrap.classList.remove('active');
      }
    };

    syncMenuMode();
    window.addEventListener('resize', syncMenuMode);
    return () => window.removeEventListener('resize', syncMenuMode);
  }, []);

  // Submenu hover control
  useEffect(() => {
    const gnbItems = document.querySelectorAll('.gnb-item');

    const handleMouseEnter = (e: Event) => {
      if (window.innerWidth >= 1280) {
        const submenu = (e.currentTarget as HTMLElement).querySelector('.sub-gnb-wrap');
        if (submenu) {
          submenu.classList.add('open');
        }
      }
    };

    const handleMouseLeave = (e: Event) => {
      if (window.innerWidth >= 1280) {
        const submenu = (e.currentTarget as HTMLElement).querySelector('.sub-gnb-wrap');
        if (submenu) {
          submenu.classList.remove('open');
        }
      }
    };

    gnbItems.forEach(item => {
      item.addEventListener('mouseenter', handleMouseEnter);
      item.addEventListener('mouseleave', handleMouseLeave);
    });

    return () => {
      gnbItems.forEach(item => {
        item.removeEventListener('mouseenter', handleMouseEnter);
        item.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);

  const toggleMobileMenu = () => {
    const gnbWrap = document.getElementById('gnb_wrap');
    if (gnbWrap) {
      gnbWrap.classList.toggle('active');
    }
  };

  const handleLanguageChange = (lang: string) => {
    void changeLanguage(lang as 'ko' | 'en' | 'ja');
  };

  return (
    <header
      id='header'
      ref={headerRef}
      className={`group ${hideBottomBorder ? '' : 'border-b border-gray-200'} ${isHeaderWhite ? 'header-white' : ''}`}
    >
      <div className='header-wrap flex h-[48px] flex-row items-center justify-between px-4 xl:h-[104px] xl:justify-center xl:gap-10 xl:px-20'>
        {/* Logo */}
        <h1 className='logo xl:shrink-0'>
          <a
            href={portalLinks.home}
            aria-label={t('header.hancomHome')}
            className="block h-[30px] w-[120px] bg-[url('/images/SDK_Logo_W.svg')] bg-contain bg-center bg-no-repeat group-[.header-white]:bg-[url('/images/SDK_Logo.svg')] xl:h-[44px] xl:w-[170px]"
          ></a>
        </h1>

        {/* Navigation Menu */}
        <div
          className='gnb-wrap absolute top-[56px] right-[8px] max-h-0 w-[128px] justify-center overflow-hidden px-6 shadow-[0_0_6px_rgba(0,0,0,0.16)] transition-all duration-300 xl:static xl:flex xl:max-h-none xl:w-auto xl:shrink-0 xl:overflow-visible xl:p-0 xl:shadow-none [.active]:block [.active]:max-h-[400px] [.active]:rounded-[16px] [.active]:bg-white [.active]:p-6'
          id='gnb_wrap'
        >
          {/* Main menu items */}
          <div className='flex flex-col gap-6 xl:flex-row xl:gap-0'>
            {mainMenuItems
              .filter(item => !isOverseas || item.showForOverseas !== false)
              .map(item => {
                const filteredSubItems = getFilteredSubItems(item.subItems, isOverseas, item.id);
                return (
                  <NavItem
                    key={item.id}
                    item={item}
                    portalLinks={portalLinks}
                    isOverseas={isOverseas}
                    filteredSubItems={filteredSubItems}
                  />
                );
              })}
          </div>

          {/* Mobile-only: My Page & Cart */}
          <div className='mt-6 flex flex-col gap-6 border-t border-black/10 pt-6 xl:hidden'>
            <a
              href={portalLinks.mypage}
              className='-ml-1 flex flex-row items-center text-[14px] transition-colors duration-200 hover:text-[#1465F4]'
            >
              <img src='/images/icons/icon_mypage.svg' alt='' className='w-[24px]' /> {t('header.myPage')}
            </a>
            <a
              href={portalLinks.cart}
              className='-ml-1 flex flex-row items-center text-[14px] transition-colors duration-200 hover:text-[#1465F4]'
            >
              <img src='/images/icons/icon_cart.svg' alt='' className='w-[24px]' /> {t('header.cart')}
            </a>
          </div>
        </div>

        {/* Right side: Language, Cart, MyPage, Mobile Menu */}
        <div className='flex flex-row items-center xl:shrink-0 xl:gap-10'>
          {/* Language Selector */}
          <LanguageSelector
            currentLanguage={i18n.language}
            onLanguageChange={handleLanguageChange}
            isOverseas={isOverseas}
          />

          {/* Desktop: Cart & MyPage */}
          <div className='flex flex-row items-center'>
            <a
              href={portalLinks.cart}
              aria-label={t('header.cart')}
              className="cart-btn hidden h-[40px] w-[40px] rounded-[4px] bg-[url('/images/icons/icon_cart_white.svg')] bg-cover bg-center bg-no-repeat group-[.header-white]:bg-[url('/images/icons/icon_cart.svg')] hover:bg-[#f5f5f5] xl:block"
            ></a>
            <a
              href={portalLinks.mypage}
              aria-label={t('header.myPage')}
              className="mypage-btn hidden h-[40px] w-[40px] rounded-[4px] bg-[url('/images/icons/icon_mypage_white.svg')] bg-cover bg-center bg-no-repeat group-[.header-white]:bg-[url('/images/icons/icon_mypage.svg')] hover:bg-[#f5f5f5] xl:block"
            ></a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMobileMenu}
            className="menu-btn h-[40px] w-[40px] bg-[url('/images/icons/icon_menu_white.svg')] bg-cover bg-center bg-no-repeat group-[.header-white]:bg-[url('/images/icons/icon_menu.svg')] xl:hidden"
          ></button>
        </div>
      </div>
    </header>
  );
};
